import { useState } from "react";
import { Link, NavLink } from "react-router-dom";
import { Menu, Search, ShoppingBag, User } from "lucide-react";
import { Sheet, SheetContent, SheetTitle } from "../ui/sheet";
import { Dialog, DialogContent, DialogTitle } from "../ui/dialog";
import { Logo } from "./Logo";
import { useCart } from "../../context/CartContext";
import { DestinationSearch } from "../shop/DestinationSearch";

export const NAV = [
  { to: "/destinations", label: "Destinations" },
  { to: "/shop", label: "Shop All" },
  { to: "/collections/funny-vacation-shirts", label: "Funny" },
  { to: "/collections/vacation-hats", label: "Hats" },
  { to: "/collections/family-vacation-shirts", label: "Family" },
  { to: "/collections/couples-vacation-apparel", label: "Couples" },
  { to: "/collections/cruise-vacation-apparel", label: "Cruise" },
  { to: "/collections/new-arrivals", label: "New" },
];

const MORE = [
  { to: "/collections/girls-trip", label: "Girls Trip" },
  { to: "/collections/guys-trip", label: "Guys Trip" },
  { to: "/collections/best-sellers", label: "Best Sellers" },
  { to: "/track-order", label: "Track Order" },
  { to: "/about", label: "About Us" },
  { to: "/contact", label: "Contact" },
  { to: "/faq", label: "FAQ" },
];

const IconBtn = ({ children, className = "", ...p }) => (
  <button className={`relative w-11 h-11 rounded-full grid place-items-center text-navy hover:bg-sun/30 transition-colors ${className}`} {...p}>
    {children}
  </button>
);

export const Header = () => {
  const { count, setOpen } = useCart();
  const [menu, setMenu] = useState(false);
  const [search, setSearch] = useState(false);
  return (
    <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-xl border-b border-border" data-testid="site-header">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 lg:h-20 flex items-center gap-4">
        <IconBtn onClick={() => setMenu(true)} aria-label="Open menu" data-testid="mobile-menu-button" className="lg:hidden -ml-2">
          <Menu className="w-6 h-6" />
        </IconBtn>
        <Link to="/" className="flex items-center gap-2 shrink-0" data-testid="header-logo-link">
          <Logo alt="Vacation Apparel logo" className="h-14 lg:h-[68px] w-auto drop-shadow-sm" />
        </Link>
        <nav className="hidden lg:flex items-center gap-1 mx-auto" aria-label="Main">
          {NAV.map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              data-testid={`nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              className={({ isActive }) =>
                `px-3 py-2 rounded-full text-[15px] font-semibold transition-colors ${isActive ? "bg-navy text-white" : "text-navy hover:bg-sun/30"}`
              }
            >
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="flex items-center gap-1 ml-auto lg:ml-0">
          <IconBtn onClick={() => setSearch(true)} aria-label="Search destinations" data-testid="header-search-button">
            <Search className="w-5 h-5" />
          </IconBtn>
          <Link to="/account" aria-label="Account" data-testid="header-account-link" className="w-11 h-11 rounded-full grid place-items-center text-navy hover:bg-sun/30 transition-colors">
            <User className="w-5 h-5" />
          </Link>
          <IconBtn onClick={() => setOpen(true)} aria-label="Open cart" data-testid="header-cart-button">
            <ShoppingBag className="w-5 h-5" />
            {count > 0 && (
              <span className="absolute -top-0.5 -right-0.5 min-w-5 h-5 px-1 rounded-full bg-tpink text-white text-[11px] font-bold grid place-items-center" data-testid="cart-count-badge">
                {count}
              </span>
            )}
          </IconBtn>
        </div>
      </div>

      <Sheet open={menu} onOpenChange={setMenu}>
        <SheetContent side="left" className="w-[86vw] max-w-sm p-0 bg-white" data-testid="mobile-menu">
          <SheetTitle className="sr-only">Menu</SheetTitle>
          <div className="ocean-gradient p-6 pb-8">
            <Logo alt="Vacation Apparel" className="h-20 w-auto" />
            <p className="font-brush text-sun text-2xl mt-2">Wear Your Vacation Vibes!</p>
          </div>
          <nav className="p-4 flex flex-col overflow-y-auto max-h-[calc(100vh-180px)]">
            {[...NAV, ...MORE].map((n) => (
              <Link
                key={n.to}
                to={n.to}
                onClick={() => setMenu(false)}
                className="px-4 py-3.5 rounded-xl text-lg font-semibold text-navy hover:bg-sand transition-colors"
                data-testid={`mobile-nav-${n.label.toLowerCase().replace(/\s+/g, "-")}`}
              >
                {n.label}
              </Link>
            ))}
          </nav>
        </SheetContent>
      </Sheet>

      <Dialog open={search} onOpenChange={setSearch}>
        <DialogContent className="grid-cols-1 [&>*]:min-w-0 w-[calc(100%-2rem)] max-w-2xl top-[20%] translate-y-0 bg-navy border-none rounded-3xl p-6 sm:p-8" data-testid="search-dialog">
          <DialogTitle className="font-brush text-4xl text-white">
            Where are you <span className="text-sun">traveling to?</span>
          </DialogTitle>
          <DestinationSearch variant="compact" autoFocus onDone={() => setSearch(false)} />
        </DialogContent>
      </Dialog>
    </header>
  );
};
